import { useToastContext } from "@ttc3k/trekker";
import {
  EnumEntityReferenceType,
  OperatorStaffDocument,
  OperatorStaffQuery,
  OrganizationStaffDocument,
  OrganizationStaffQuery,
  RoleType,
  useOperatorStaffQuery,
  useOrganizationStaffQuery,
  useUserApproveAccessMutation,
  useUserCreateInviteMutation,
  useUserRevokeInviteMutation,
} from "gql/generated";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { IInviteStaffMember } from "shared/components";
import { TagAction } from "../StaffCard/useStaffCard";

type EmailInviteInfo = {
  email: string;
  firstName: string;
  lastName: string;
};

export type UseStaffPageContentProps = {
  _entityId: string;
  type: EnumEntityReferenceType;
  staffGetter: typeof useOrganizationStaffQuery | typeof useOperatorStaffQuery;
};

export const useStaffPageContent = ({
  _entityId,
  type,
  staffGetter,
}: UseStaffPageContentProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  // When we're updating a staff member, we use this ID to show a spinner instead of the (...) button
  const [updatingId, setUpdatingId] = useState<string | undefined>();

  const { data, error, loading, refetch } =
    type === EnumEntityReferenceType.Operator
      ? (staffGetter as typeof useOperatorStaffQuery)({
          variables: { operatorId: _entityId },
        })
      : (staffGetter as typeof useOrganizationStaffQuery)({
          variables: { organizationId: _entityId },
        });

  // The mutation to create or resend an invite use the same params
  const [createOrResendInvite] = useUserCreateInviteMutation();

  // When a user access request is accepted
  const [acceptAccessRequest] = useUserApproveAccessMutation();

  // When a user is removed, we need to refetch the data to get the updated list
  const [revokeInvite] = useUserRevokeInviteMutation();

  /**
   * Handle opening / closing of the modal
   */
  const [isInviteStaffModalOpen, setIsInviteStaffModalOpen] = useState(false);
  const handleInviteStaffModalClick = useCallback(
    () => setIsInviteStaffModalOpen((prev) => !prev),
    [],
  );

  /**
   * Accept the user's request to join the business
   * @param id
   */
  const handleAcceptClick = async (id: string, email: string) => {
    setUpdatingId(id);
    try {
      await acceptAccessRequest({
        variables: {
          userId: id,
          entity: {
            _entityId: _entityId,
            type,
          },
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:STAFF.SUCCESS.ACCEPTED", { email }),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:STAFF.ERROR.ACCEPTING", {
          email: id,
        }),
      });
    } finally {
      setUpdatingId(undefined);
    }
  };

  /**
   * Handle creating staff invites
   * - These are sent one at a time, instead of a group, so we can get a toast notification
   *   for each staff member that is being added
   * @param staffToInvite
   */
  const handleInviteStaffClick = async (
    sendInvitationEmail: boolean,
    staffToInvite: IInviteStaffMember[],
  ) => {
    await Promise.all(
      staffToInvite.map((toInvite) =>
        handleCreateOrResendInviteClick(toInvite, true, sendInvitationEmail),
      ),
    );
    await refetch();
  };

  /**
   * Remove the staff member and refetch the data
   * @param id
   */
  const handleRemoveClick = async (id: string, email: string) => {
    setUpdatingId(id);
    try {
      await revokeInvite({
        variables: {
          userId: id,
          entity: {
            _entityId: _entityId,
            type,
          },
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("core:STAFF.SUCCESS.REMOVED", { email }),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:STAFF.ERROR.REMOVING", { email }),
      });
    } finally {
      setUpdatingId(undefined);
    }
  };

  /**
   * Creates a new invite OR resends an invite
   * @param param0
   */
  const handleCreateOrResendInviteClick = async (
    { email, firstName, lastName }: EmailInviteInfo,
    isCreating: boolean = false,
    sendInvitationEmail: boolean,
  ) => {
    setUpdatingId(email);
    try {
      await createOrResendInvite({
        variables: {
          email,
          firstName,
          lastName,
          sendInvitationEmail,
          entity: {
            _entityId,
            type,
          },
          role: RoleType.Staff,
        },
        refetchQueries: [
          type === EnumEntityReferenceType.Operator
            ? OperatorStaffDocument
            : OrganizationStaffDocument,
        ],
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: sendInvitationEmail
          ? t("core:STAFF.SUCCESS.STAFF_INVITED", { email })
          : t("core:STAFF.SUCCESS.STAFF_ADDED", { email }),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: isCreating
          ? t("core:STAFF.ERROR.INVITING", { email })
          : t("core:STAFF.ERROR.RESENDING", { email }),
      });
    } finally {
      setUpdatingId(undefined);
    }
  };

  /**
   * Handle opening the remove/delete confirmation modal and
   * storing the values for the user that is being removed/deleted
   */
  const [confirmModalInfo, setConfirmModalInfo] = useState<
    | {
        action: TagAction;
        userId: string;
        email: string;
      }
    | undefined
  >();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const handleOpenConfirmationModal = (
    action: TagAction,
    userId: string,
    email: string,
  ) => {
    setConfirmModalInfo({ action, userId, email });
    setIsConfirmationModalOpen(true);
  };

  /**
   * Handle the remove/delete confirmation modal No/Yes click
   * @param confirmed
   */
  const handleConfirmationModalActionClick = (confirmed: boolean) => {
    if (confirmed && confirmModalInfo) {
      handleRemoveClick(confirmModalInfo.userId, confirmModalInfo.email);
    }
    setIsConfirmationModalOpen(false);
  };

  return {
    staff:
      type === EnumEntityReferenceType.Operator
        ? (data as OperatorStaffQuery)?.operatorStaff
        : (data as OrganizationStaffQuery)?.organizationStaff,
    error,
    loading,
    updatingId,
    confirmModalInfo,
    isConfirmationModalOpen,
    isInviteStaffModalOpen,
    handleAcceptClick,
    handleInviteStaffModalClick,
    handleInviteStaffClick,
    handleOpenConfirmationModal,
    handleConfirmationModalActionClick,
    handleCreateOrResendInviteClick,
  };
};
